import { CloseCircleFilled } from '@ant-design/icons';
import { Select, Spin } from 'antd';
import clsx from 'clsx';
import {
  debounce,
  filter,
  get,
  includes,
  map,
  trim,
  uniq,
  uniqBy,
} from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { GlobeIcon } from '../assets/svg';
import useLazyQueryWithCallbacks from '../common/CommonUseLazyQuery';
import {
  DROPDOWN_LIMIT,
  INITIAL_CURRENT_PAGE,
  SCROLL_CONST,
} from '../common/constants';
import usePrevious from '../common/usePrevious';
import CommonTooltip from './CommonTooltip';

let searchDebounce = null;
const CommonDropdown = (props) => {
  const {
    query,
    variables = {},
    responsePath,
    uniqueByLabel = 'id',
    valuePath,
    labelPath,
    optionKey,
    callback,
    fetchPolicy,
    hasSelectAll,
    showSearch,
    useEffectDeps = [],
    conditionToCheckBeforeQuery = true,
    onChange = () => {},
    isSelectedAll,
    currentPage: currPage = 0,
    className,
    autoFocus = false,
    customOptions = [],
    showExtraLabel = false,
    showExtraLoader = false,
    filterDataById = '',
    addExtraData,
    getPopupContainer,
    dropdownMatchSelectWidth,
    disableCondition,
    compactDisplay = false,
    compactLabel = '',
    compactIcon = '',
    ...rest
  } = props;
  const [listData, setListData] = useState([]);
  const [currentPage, setCurrentPage] = useState(currPage);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(rest?.projectId);
  const prevRestProjectId = usePrevious(rest?.projectId);
  const [loading, setLoading] = useState(!props?.disabled);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isOverlayHovered, setIsOverlayHovered] = useState(false);
  const selectRef = useRef(null);
  const compactOverlayRef = useRef(null);
  const [fetchListData] = useLazyQueryWithCallbacks(query, {
    fetchPolicy,
    onCompleted: (res) => {
      const data = get(res, responsePath, []);
      if (currentPage === 0) {
        setListData(data);
      } else {
        setListData((oldData) => uniqBy([...oldData, ...data], valuePath));
      }
      if (listData && filterDataById) {
        if (typeof filterDataById === 'string') {
          setListData((oldData) =>
            filter(oldData, (item) => item?.id !== filterDataById),
          );
        } else {
          setListData((oldData) =>
            filter(oldData, (item) => !includes(filterDataById, item?.id)),
          );
        }
      }
      if (addExtraData) {
        setListData((oldData) => uniq([...oldData, ...addExtraData]));
      }
      if (currentProjectId !== prevRestProjectId) {
        setCurrentPage(0);
        setCurrentProjectId(rest?.projectId);
      } else {
        setCurrentPage((page) => page + 1);
      }
      setHasMore(data?.length >= DROPDOWN_LIMIT);
      if (typeof callback === 'function') {
        if (addExtraData) {
          callback(res, addExtraData);
        } else {
          callback(res);
        }
      }
      setLoading(false);
    },
    onError() {
      setLoading(false);
    },
  });
  useEffect(() => {
    if (prevRestProjectId !== rest?.projectId) {
      setCurrentPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest?.projectId]);

  useEffect(() => {
    if (conditionToCheckBeforeQuery) {
      setCurrentPage(INITIAL_CURRENT_PAGE);
      fetchListData({
        fetchPolicy,
        variables: {
          ...variables,
          filter: {
            ...(variables?.filter || {}),
            skip: INITIAL_CURRENT_PAGE * DROPDOWN_LIMIT,
            limit: DROPDOWN_LIMIT,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, useEffectDeps);

  const handleScroll = (event) => {
    const { target } = event;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};
    const scrolledToBottom =
      scrollTop + offsetHeight >= scrollHeight - SCROLL_CONST;
    if (scrolledToBottom && hasMore && !loading) {
      const obj = {
        variables: {
          ...variables,
          filter: {
            ...(variables?.filter || {}),
            skip: currentPage * DROPDOWN_LIMIT,
            limit: DROPDOWN_LIMIT,
            search: searchText,
          },
        },
      };
      fetchListData(obj);
    }
  };
  const searchQuery = (search) => {
    setSearchText(trim(search));
    setCurrentPage(0);
    fetchListData({
      fetchPolicy: 'network-only',
      variables: {
        ...variables,
        filter: {
          ...(variables?.filter || {}),
          search: trim(search),
          skip: 0,
          limit: DROPDOWN_LIMIT,
        },
      },
    });
  };
  const handleSearch = (value) => {
    if (searchDebounce) {
      searchDebounce.cancel();
      searchDebounce = null;
    }
    searchDebounce = debounce(searchQuery, 500);
    searchDebounce(trim(value));
  };
  const handleClear = () => {
    if (searchText) {
      searchQuery();
    }
  };
  const getLabel = (optionLabel, item) => {
    if (showExtraLabel) {
      return (
        <div className="d-flex align-center">
          {optionLabel}
          {item?.projectAgencies?.length < 1 && <GlobeIcon className="ml-10" />}
        </div>
      );
    }
    if (typeof labelPath === 'object') {
      return <div className="white-space-break-spaces">{optionLabel}</div>;
    }
    return optionLabel;
  };

  const getCompactDisplayText = () => {
    const selectedCount = Array.isArray(rest?.value) ? rest?.value?.length : 0;
    const totalCount = listData?.length || 0;
    if (selectedCount === 0) {
      // Special case for Teams - show "Select Your Team"
      if (compactLabel === 'Teams') {
        return 'Select Your Teams';
      }
      return `Select ${compactLabel || 'Items'}`;
    }
    const isAllSelected = selectedCount === totalCount && totalCount > 0;
    if (isAllSelected) {
      // Special case for Teams - show "All Teams Selected"
      if (compactLabel === 'Teams') {
        return 'All Teams Selected';
      }
      return `All ${compactLabel}`;
    }
    // Fix singular/plural text for count
    const labelText = compactLabel || 'Items';
    const singularLabel = labelText?.endsWith('s')
      ? labelText?.slice(0, -1)
      : labelText;
    const displayLabel = selectedCount === 1 ? singularLabel : labelText;

    // Special case for Teams - add "Selected" suffix
    if (compactLabel === 'Teams') {
      return `${selectedCount} ${displayLabel} Selected`;
    }

    return `${selectedCount} ${displayLabel}`;
  };

  const getSelectedItemNames = () => {
    if (!Array.isArray(rest?.value) || rest?.value?.length === 0) {
      return '';
    }

    const selectedItems = filter(listData, (item) => {
      return includes(rest?.value, get(item, valuePath));
    });

    const names = map(selectedItems, (item) => {
      if (typeof labelPath === 'object') {
        return map(labelPath, (label) => get(item, label))?.join(' - ');
      }
      return get(item, labelPath);
    });

    return names?.length > 0 ? names?.join(', ') : '';
  };

  const isDOMNodeWithContains = (element) => {
    return (
      element &&
      typeof element?.contains === 'function' &&
      element?.nodeType === 1
    );
  };

  const getSelectDOMElement = (selectReference) => {
    if (!selectReference?.current) return null;

    const selectComponent = selectReference.current;

    if (isDOMNodeWithContains(selectComponent)) {
      return selectComponent;
    }

    if (
      selectComponent?.nativeElement &&
      isDOMNodeWithContains(selectComponent.nativeElement)
    ) {
      return selectComponent.nativeElement;
    }

    if (
      selectComponent?.selectRef?.current &&
      isDOMNodeWithContains(selectComponent.selectRef.current)
    ) {
      return selectComponent.selectRef.current;
    }

    // eslint-disable-next-line no-undef
    const selectWrapper = document.querySelector(
      '.common-dropdown-wrapper .ant-select',
    );
    if (isDOMNodeWithContains(selectWrapper)) {
      return selectWrapper;
    }

    return null;
  };

  // Helper function to check if click is within the select element
  const isClickWithinSelectElement = (target) => {
    const selectElement = getSelectDOMElement(selectRef);
    if (!selectElement) return false;

    try {
      return selectElement.contains(target);
    } catch {
      return target === selectElement;
    }
  };

  // Helper function to check if click is within the compact overlay
  const isClickWithinCompactOverlay = (target) => {
    const compactOverlay = compactOverlayRef?.current;
    if (!isDOMNodeWithContains(compactOverlay)) return false;

    try {
      return compactOverlay.contains(target);
    } catch {
      return target === compactOverlay;
    }
  };

  // Helper function to check if click is within the dropdown menu
  const isClickWithinDropdownMenu = (target) => {
    const dropdownElement = target.closest?.('.ant-select-dropdown');
    return Boolean(dropdownElement);
  };

  // Helper function to check if click is within custom popup container
  const isClickWithinCustomContainer = (target) => {
    if (typeof getPopupContainer !== 'function') return false;

    const selectElement = getSelectDOMElement(selectRef);
    if (!selectElement) return false;

    try {
      const customContainer = getPopupContainer(selectElement);
      return (
        isDOMNodeWithContains(customContainer) &&
        customContainer.contains(target)
      );
    } catch {
      return false;
    }
  };

  // Helper function to check if click is on control buttons (clear, arrow)
  const isClickOnControlButton = (target) => {
    const controlSelectors = [
      '.ant-select-clear',
      '.anticon-close',
      '.ant-select-arrow',
    ];

    return controlSelectors.some(
      (selector) =>
        target.closest?.(selector) ||
        target.classList?.contains(selector.slice(1)),
    );
  };

  // Handle clicks outside the dropdown in compact mode
  useEffect(() => {
    // Only activate click outside handler when compact mode is enabled and dropdown is open
    if (!compactDisplay || !isDropdownOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      const clickTarget = event?.target;

      // Early exit if click target is invalid
      if (!clickTarget || !isDOMNodeWithContains(clickTarget)) {
        return;
      }

      // Check if click is within any component elements that should keep dropdown open
      const shouldKeepDropdownOpen =
        isClickWithinSelectElement(clickTarget) ||
        isClickWithinCompactOverlay(clickTarget) ||
        isClickWithinDropdownMenu(clickTarget) ||
        isClickWithinCustomContainer(clickTarget) ||
        isClickOnControlButton(clickTarget);

      // Close dropdown if click is outside all relevant elements
      if (!shouldKeepDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    // Add event listener with capture phase to handle clicks before they bubble
    // eslint-disable-next-line no-undef
    document?.addEventListener?.('mousedown', handleClickOutside, true);

    return () => {
      // eslint-disable-next-line no-undef
      document?.removeEventListener?.('mousedown', handleClickOutside, true);
    };
  }, [compactDisplay, isDropdownOpen, getPopupContainer]);

  const handleCompactDisplayClick = (e) => {
    e?.stopPropagation();
    e?.preventDefault();

    if (!rest?.disabled && !loading) {
      setIsDropdownOpen((prevOpen) => !prevOpen);
    }
  };

  const handleCompactClearClick = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setIsOverlayHovered(false);
    setIsDropdownOpen(false);
    if (!rest?.disabled && !loading) {
      onChange(rest?.mode === 'multiple' ? [] : undefined, []);
      if (typeof rest?.onClear === 'function') {
        rest.onClear();
      }
      handleClear();
    }
  };

  const shouldShowCompactClear = () => {
    if (!rest?.allowClear) return false;

    if (rest?.mode === 'multiple') {
      return Array.isArray(rest?.value) && rest?.value?.length > 0;
    }

    return (
      rest?.value !== undefined && rest?.value !== null && rest?.value !== ''
    );
  };

  const handleDropdownOpenChange = (open) => {
    setIsDropdownOpen(open);
  };

  return (
    <div className="common-dropdown-wrapper position-relative width-percent-100">
      <Spin spinning={showExtraLoader && loading} size="small">
        {compactDisplay && (
          <CommonTooltip
            title={getSelectedItemNames()}
            open={!isDropdownOpen && isOverlayHovered}
            destroyOnHidden
            onMouseEnter={() => {
              if (getSelectedItemNames()) {
                setIsOverlayHovered(true);
              }
            }}
            onMouseLeave={() => {
              setIsOverlayHovered(false);
            }}
          >
            <div
              ref={compactOverlayRef}
              className={clsx(
                'compact-display-overlay d-flex align-center',
                getSelectedItemNames() && 'has-tooltip-content',
                !rest?.disabled && 'clickable',
                rest?.disabled && 'disabled',
                shouldShowCompactClear() && 'has-clear-button',
                isOverlayHovered && 'is-hovered',
              )}
              onClick={handleCompactDisplayClick}
            >
              {compactIcon && (
                <span className="compact-icon mr-5">{compactIcon}</span>
              )}
              <span className="compact-text">{getCompactDisplayText()}</span>
              {isOverlayHovered && (
                <span
                  className="compact-clear-icon"
                  onClick={handleCompactClearClick}
                  role="button"
                  tabIndex={0}
                  aria-label="Clear selection"
                >
                  <CloseCircleFilled />
                </span>
              )}
            </div>
          </CommonTooltip>
        )}
        <Select
          ref={selectRef}
          loading={loading}
          open={compactDisplay ? isDropdownOpen : undefined}
          onOpenChange={compactDisplay ? handleDropdownOpenChange : undefined}
          onPopupScroll={handleScroll}
          showSearch={showSearch}
          onSearch={showSearch && handleSearch}
          onClear={handleClear}
          onBlur={handleClear}
          className={clsx(
            className,
            hasSelectAll && 'has-select-all',
            !searchText && loading && 'no-tag-render',
            compactDisplay && 'compact-mode',
          )}
          filterOption={(input, option) => {
            const user = get(option, 'data-name', '');
            return user?.toLowerCase()?.includes(trim(input?.toLowerCase()));
          }}
          getPopupContainer={(trigger) =>
            typeof getPopupContainer === 'function'
              ? getPopupContainer(trigger)
              : trigger.parentElement
          }
          onChange={(e) => {
            let records = [];
            if (typeof e === 'object') {
              records = filter(listData, (item) => {
                return includes(e, get(item, valuePath));
              });
            } else {
              records = filter(listData, (item) => {
                return e === get(item, valuePath);
              });
            }
            onChange(e, records);
            handleClear();
          }}
          autoFocus={autoFocus}
          popupMatchSelectWidth={dropdownMatchSelectWidth}
          options={[
            ...(!searchText && hasSelectAll && listData?.length
              ? [
                  {
                    value: 'all',
                    label: (
                      <span>
                        {isSelectedAll ? 'Selected All' : 'Select All'}
                      </span>
                    ),
                  },
                ]
              : []),
            ...map(
              uniqBy([...listData, ...customOptions], uniqueByLabel),
              (item, index) => {
                const optionValue = get(item, valuePath);
                const optionLabel =
                  typeof labelPath === 'object'
                    ? map(labelPath, (label) => get(item, label)).join(' - ')
                    : get(item, labelPath);
                const isDisabled =
                  typeof disableCondition === 'function'
                    ? disableCondition(item)
                    : false;
                return {
                  key: `${optionKey}-${optionValue}-${index}`,
                  value: optionValue,
                  label: getLabel(optionLabel, item),
                  'data-name': optionLabel,
                  disabled: isDisabled,
                };
              },
            ),
          ]}
          {...rest}
        />
      </Spin>
    </div>
  );
};

export default CommonDropdown;
